/**
 * Multi-turn conversation results: parsing uploaded result files into conversations,
 * and the reviewer-feedback model shared by the API and the admin UI.
 *
 * Two file shapes are supported:
 *  - Scenario reports (CSV with `turn_transcript`): one judged conversation per row.
 *  - Turn logs (router CSV with `router_label`, classifier CSV with `classifier_verdict`,
 *    or deepteam JSON): one row per turn, grouped into conversations.
 */

export type TurnOutcome = "flagged" | "passed" | "abort";

export interface SmeFields {
  agree?: string;
  correct?: string;
  notes?: string;
}

export interface Round {
  sent: string;
  reply: string;
  verdict: string | null;
  reason: string;
  latencyMs: number | null;
  code: string | null;
  outcome: TurnOutcome;
  sme: SmeFields | null;
}

export interface RunConversation {
  kind: "run";
  id: string;
  source: string;
  attack: string;
  category: string;
  stop: Record<string, string> | null;
  rounds: Round[];
}

export interface ScenarioTurn {
  user: string;
  who: string;
  reply: string;
}

export interface ScenarioConversation {
  kind: "scenario";
  id: string;
  source: string;
  scenarioId: string;
  cat: string;
  catName: string;
  area: string;
  label: string;
  question: string;
  rubric: string;
  goal: string;
  result: string;
  mode: string;
  severity: string;
  infra: string;
  turnCount: string;
  turns: ScenarioTurn[];
  judge: string;
}

export type Conversation = RunConversation | ScenarioConversation;

// ---------------------------------------------------------------- text repair

// Repairs UTF-8 text that was decoded as Windows-1252/Latin-1 upstream ("â€”" -> "—"). Only runs that
// re-decode as valid UTF-8 are replaced, so correctly encoded characters in the same file are kept.
// ponytail: a sequence whose continuation bytes were stripped entirely (a bare "â") cannot be recovered.
const CP1252: Record<string, number> = {
  "€": 0x80, "‚": 0x82, "ƒ": 0x83, "„": 0x84, "…": 0x85, "†": 0x86, "‡": 0x87, "ˆ": 0x88, "‰": 0x89,
  "Š": 0x8a, "‹": 0x8b, "Œ": 0x8c, "Ž": 0x8e, "‘": 0x91, "’": 0x92, "“": 0x93, "”": 0x94, "•": 0x95,
  "–": 0x96, "—": 0x97, "˜": 0x98, "™": 0x99, "š": 0x9a, "›": 0x9b, "œ": 0x9c, "ž": 0x9e, "Ÿ": 0x9f,
};
const MOJIBAKE = /[Â-ô][\u0080-¿€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]{1,3}/g;
export function fixMojibake(text: string): string {
  // Created per call: some test DOMs (jsdom) have no TextDecoder at import time.
  const utf8 = new TextDecoder("utf-8", { fatal: true });
  return text.replace(MOJIBAKE, (m) => {
    try {
      return utf8.decode(Uint8Array.from([...m], (c) => CP1252[c] ?? c.charCodeAt(0)));
    } catch {
      return m;
    }
  });
}

// ---------------------------------------------------------------- CSV

export function parseCsv(text: string): Record<string, string>[] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < normalized.length; i++) {
    const c = normalized[i];
    if (quoted) {
      if (c === '"') {
        if (normalized[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else field += c;
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  const [head, ...rest] = rows;
  if (!head) return [];
  const keys = head.map((k) => k.replace(/^﻿/, "").trim());
  return rest
    .filter((r) => r.length > 1)
    .map((r) => Object.fromEntries(keys.map((k, i) => [k, r[i] ?? ""])));
}

// "T1 student: … | Lincoln: …\nT2 student: …" -> turns
function parseTranscript(t: string): ScenarioTurn[] {
  return String(t || "")
    .replace(/\r/g, "")
    .split(/(?:^|\n)T\d+ student: /)
    .slice(1)
    .map((p) => {
      const m = p.match(/^([\s\S]*?) \| ([A-Z][\w .'-]{0,30}): ([\s\S]*)$/);
      return m ? { user: m[1] ?? "", who: m[2] ?? "Persona", reply: m[3] ?? "" } : { user: p, who: "Persona", reply: "" };
    });
}

// ---------------------------------------------------------------- turns

const INFRA_REASONS = new Set(["parse_error", "classification_error"]);
const HARMFUL_VERDICTS = new Set(["harmful", "unsafe", "flagged", "blocked", "fail", "true"]);

/** Harmful route/verdict = flagged; infra reasons = abort; anything else = passed to the persona. */
export function turnOutcome(verdict: string | null | undefined, reason: string | null | undefined): TurnOutcome {
  if (!verdict) return "abort";
  const r = reason || "";
  if (INFRA_REASONS.has(r) || r.startsWith("safety_error") || r.startsWith("fetch_error")) return "abort";
  return HARMFUL_VERDICTS.has(String(verdict).trim().toLowerCase()) ? "flagged" : "passed";
}

function round(
  sent: unknown, reply: unknown, verdict: unknown, reason: unknown,
  latencyMs: number | null = null, code: string | null = null, sme: SmeFields | null = null,
): Round {
  const v = verdict ? String(verdict) : null;
  const r = String(reason ?? "");
  return { sent: String(sent ?? ""), reply: String(reply ?? ""), verdict: v, reason: r, latencyMs, code, sme, outcome: turnOutcome(v, r) };
}

interface TurnRow {
  source: string;
  attack: string;
  category: string;
  turn: number;
  stop: Record<string, string> | null;
  round: Round;
}

// Rows may interleave across runs; a turn-1 row starts a new run for its source/attack/category key.
function groupTurns(rows: TurnRow[]): Omit<RunConversation, "id">[] {
  const out: Omit<RunConversation, "id">[] = [];
  const open = new Map<string, Omit<RunConversation, "id">>();
  for (const x of rows) {
    const key = `${x.source}|${x.attack}|${x.category}`;
    let run = open.get(key);
    if (!run || x.turn === 1) {
      run = { kind: "run", source: x.source, attack: x.attack, category: x.category, stop: x.stop, rounds: [] };
      open.set(key, run);
      out.push(run);
    } else if (x.stop) {
      run.stop = { ...(run.stop ?? {}), ...x.stop };
    }
    run.rounds.push(x.round);
  }
  return out;
}

const CSV_COLUMNS = [
  "scenario_id", "bri_category", "bri_category_name", "internal_category", "label", "question", "rubric", "adversary_goal",
  "result", "failure_mode", "severity", "infra_error", "turn_count", "turn_transcript", "judge_reasoning",
  "model", "attack", "category_code", "category_label", "round", "attacker_message", "persona_reply", "classifier_verdict",
  "classifier_reason", "latency_ms", "sme_agree", "sme_correct_verdict", "sme_notes",
  "attack_category", "turn", "sentMessage", "targetReply", "router_label", "router_reason", "router_code", "run_outcome",
] as const;
type CsvRow = Record<(typeof CSV_COLUMNS)[number], string>;
const BLANK_ROW = Object.fromEntries(CSV_COLUMNS.map((k) => [k, ""])) as CsvRow;

const fileSource = (fileName: string) => fileName.replace(/\.(json|csv)$/i, "").replace(/^deepteam-results-/, "");
const num = (v: unknown) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

// ---------------------------------------------------------------- files

/**
 * Parses an uploaded results file into conversations with stable ids (`c0001`, …, in file order).
 * Throws an Error with a user-facing message when the format is not recognised.
 */
export function parseResultsFile(fileName: string, rawText: string): Conversation[] {
  const source = fileSource(fileName);
  const text = fixMojibake(rawText);
  let items: Omit<Conversation, "id">[];

  if (/\.json$/i.test(fileName)) {
    const data = JSON.parse(text);
    if (!Array.isArray(data)) throw new Error("Expected a JSON array.");
    if (data.length && Array.isArray(data[0].rounds)) {
      items = data.map((r: any) => ({
        kind: "run" as const, source, attack: String(r.attack ?? ""), category: String(r.category ?? ""), stop: r.stop_info ?? null,
        rounds: r.rounds.map((x: any) => round(x.sentMessage, x.targetReply, x.safetyRoute?.category, x.safetyRoute?.reason, num(x.safetyLatencyMs))),
      }));
    } else {
      items = groupTurns(data.map((x: any) => ({
        source, attack: String(x.strategy ?? ""), category: String(x.category ?? ""), turn: Number(x.round), stop: null,
        round: round(x.sentMessage, x.targetReply, x.safetyRoute?.category, x.safetyRoute?.reason),
      })));
    }
  } else {
    const parsed = parseCsv(text);
    const cols = parsed[0];
    if (!cols) throw new Error("The file has no rows.");
    const normalizedCols = new Set(Object.keys(cols).map((k) => k.trim().toLowerCase()));
    // Normalize row keys to allow case-insensitive access to known columns
    const rows = parsed.map((r) => {
      const lower = Object.fromEntries(Object.entries(r).map(([k, v]) => [k.trim().toLowerCase(), v]));
      const mapped: Record<string, string> = { ...BLANK_ROW };
      for (const col of CSV_COLUMNS) {
        const val = r[col] ?? lower[col.toLowerCase()] ?? "";
        if (val) mapped[col] = val;
      }
      return mapped as CsvRow;
    });
    if (normalizedCols.has("turn_transcript")) {
      items = rows.map((r) => ({
        kind: "scenario" as const, source, scenarioId: r.scenario_id, cat: r.bri_category, catName: r.bri_category_name,
        area: r.internal_category, label: r.label, question: r.question, rubric: r.rubric, goal: r.adversary_goal,
        result: r.result, mode: r.failure_mode, severity: r.severity, infra: r.infra_error, turnCount: r.turn_count,
        turns: parseTranscript(r.turn_transcript), judge: r.judge_reasoning,
      }));
    } else if (normalizedCols.has("classifier_verdict")) {
      // One row per round, several models per file: each model becomes its own source.
      items = groupTurns(rows.map((r) => ({
        source: r.model || source, attack: r.attack,
        category: [r.category_code, r.category_label].filter(Boolean).join(" "), turn: Number(r.round), stop: null,
        round: round(r.attacker_message, r.persona_reply, r.classifier_verdict, r.classifier_reason, num(r.latency_ms), r.classifier_verdict || null,
          r.sme_agree || r.sme_correct_verdict || r.sme_notes ? { agree: r.sme_agree, correct: r.sme_correct_verdict, notes: r.sme_notes } : null),
      })));
    } else if (normalizedCols.has("router_label")) {
      items = groupTurns(rows.map((r) => ({
        source, attack: r.attack, category: r.attack_category, turn: Number(r.turn),
        stop: r.run_outcome ? { run_outcome: r.run_outcome } : null,
        round: round(r.sentMessage, r.targetReply, r.router_label || null, r.router_reason, null, r.router_code || null),
      })));
    } else {
      throw new Error("Unrecognised columns: expected turn_transcript, router_label or classifier_verdict.");
    }
  }
  if (!items.length) throw new Error("The file has no conversations.");
  return items.map((c, i) => ({ ...c, id: "c" + String(i + 1).padStart(4, "0") }) as Conversation);
}

// ---------------------------------------------------------------- reviews

export const REVIEW_RATINGS = ["good", "acceptable", "poor"] as const;
export type ReviewRating = (typeof REVIEW_RATINGS)[number];
export const REVIEW_RATING_LABELS: Record<ReviewRating, string> = { good: "Good", acceptable: "Acceptable", poor: "Poor" };
export const REVIEW_NOTE_MAX = 2000;

export interface ReviewEntry {
  rating?: ReviewRating | null;
  note?: string;
}

/** One reviewer's feedback on one conversation's persona replies. Turn keys are 1-based turn numbers. */
export interface ConversationReview {
  conversationId: string;
  userId: string;
  reviewerEmail?: string;
  turns: Record<string, ReviewEntry>;
  overall: ReviewEntry;
  updatedAt?: string;
}

export const reviewHasContent = (r: Pick<ConversationReview, "turns" | "overall">) =>
  [r.overall, ...Object.values(r.turns || {})].some((e) => e?.rating || e?.note);

function sanitizeEntry(e: unknown): ReviewEntry {
  const x = (e && typeof e === "object" ? e : {}) as Record<string, unknown>;
  const rating = REVIEW_RATINGS.includes(x.rating as ReviewRating) ? (x.rating as ReviewRating) : null;
  const note = typeof x.note === "string" ? x.note.trim().slice(0, REVIEW_NOTE_MAX) : "";
  return { rating, note };
}

/**
 * Validates a review body from the client against the conversation's turn count.
 * Unknown ratings are dropped, notes are trimmed and capped, turns outside 1..turnCount are rejected.
 */
export function sanitizeReview(body: unknown, turnCount: number): Pick<ConversationReview, "turns" | "overall"> {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const rawTurns = (b.turns && typeof b.turns === "object" ? b.turns : {}) as Record<string, unknown>;
  const turns: Record<string, ReviewEntry> = {};
  for (const [k, v] of Object.entries(rawTurns)) {
    const n = Number(k);
    if (!Number.isInteger(n) || n < 1 || n > turnCount) throw new Error(`Turn ${k} is not part of this conversation.`);
    turns[String(n)] = sanitizeEntry(v);
  }
  return { turns, overall: sanitizeEntry(b.overall) };
}

export const conversationTurnCount = (c: Conversation) => (c.kind === "run" ? c.rounds.length : c.turns.length);

/**
 * Reviews are blind: a reviewer only receives their own reviews, plus how many reviewers have reviewed each
 * conversation. Super admins receive everyone's reviews (for the full exported report).
 */
export function reviewsForViewer(
  reviews: ConversationReview[],
  viewerId: string,
  canSeeAll: boolean,
): { reviews: ConversationReview[]; reviewCounts: Record<string, number>; reviewScope: "all" | "own" } {
  const withContent = reviews.filter(reviewHasContent);
  const reviewCounts: Record<string, number> = {};
  for (const r of withContent) reviewCounts[r.conversationId] = (reviewCounts[r.conversationId] ?? 0) + 1;
  return {
    reviews: canSeeAll ? withContent : withContent.filter((r) => r.userId === viewerId),
    reviewCounts,
    reviewScope: canSeeAll ? "all" : "own",
  };
}
